<template>
  <b-form class="grc-form" @submit.prevent="submitForm">
    <b-alert
      v-if="localForm.secondLevelApprover && approvalLabel"
      show
      variant="info"
      class="grc-form__alert"
    >
      Aprovação: {{ approvalLabel }}
    </b-alert>

    <b-alert
      v-if="submitValidationError"
      show
      variant="warning"
      class="grc-form__alert py-2"
    >
      {{ submitValidationError }}
    </b-alert>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Identificação e avaliação</h3>
        <p class="grc-form__section-description">
          Consolide o risco, sua criticidade e os elementos centrais da análise.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="8">
          <b-form-group label="Título do risco *">
            <b-form-input
              v-model.trim="localForm.title"
              required
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Status *">
            <b-form-select
              v-model="localForm.status"
              :options="statusOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Probabilidade *">
            <b-form-select
              v-model="localForm.probability"
              :options="scaleOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Impacto *">
            <b-form-select
              v-model="localForm.impact"
              :options="scaleOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Estratégia de tratamento *">
            <b-form-select
              v-model="localForm.treatmentStrategy"
              :options="treatmentOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Evento de risco *">
        <b-form-textarea
          v-model.trim="localForm.event"
          rows="2"
          required
        ></b-form-textarea>
      </b-form-group>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Causas">
            <b-form-textarea
              v-model.trim="localForm.causes"
              rows="3"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Consequências">
            <b-form-textarea
              v-model.trim="localForm.consequences"
              rows="3"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Origem e aplicabilidade</h3>
        <p class="grc-form__section-description">
          Estruture a fonte do risco, sua consequência e o enquadramento
          aplicável.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Tipo de consequência">
            <b-form-select
              v-model="localForm.consequenceType"
              :options="consequenceOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Tipo de fonte *">
            <b-form-select
              v-model="localForm.sourceType"
              :options="sourceTypeOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Fonte *">
            <b-form-input
              v-model.trim="localForm.sourceName"
              required
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Identificador da fonte">
            <b-form-input
              v-model.trim="localForm.sourceIdentifier"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Jurisdição">
            <b-form-input
              v-model.trim="localForm.jurisdiction"
              :required="localForm.sourceType === 'legal-regulatory'"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Aplicabilidade *">
            <b-form-select
              v-model="localForm.applicationType"
              :options="applicationTypeOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">
          Contexto organizacional e vínculos
        </h3>
        <p class="grc-form__section-description">
          Relacione o risco à área, responsáveis e demais itens conectados.
        </p>
      </div>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Stakeholders afetados">
            <b-form-input
              v-model.trim="localForm.stakeholdersAffected"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Núcleo/Categoria interna">
            <b-form-input
              v-model.trim="localForm.internalCategory"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Área responsável *">
            <b-form-select
              v-model="localForm.area"
              :options="areaOptions"
              required
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Escopo">
            <b-form-select
              v-model="localForm.scope"
              :options="scopeOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Dono do risco">
            <b-form-select
              v-model="localForm.owner"
              :options="userOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Gestor do risco">
            <b-form-select
              v-model="localForm.manager"
              :options="userOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Aprovador de segundo nível">
            <b-form-select
              v-model="localForm.secondLevelApprover"
              :options="userOptionsWithEmpty"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Periodicidade de revisão (meses)">
            <b-form-input
              v-model="localForm.periodicityMonths"
              type="number"
              min="1"
              max="60"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Projetos vinculados">
        <SearchableSelect
          v-model="localForm.projects"
          :options="projectOptions"
          multiple
          placeholder="Selecione um ou mais projetos"
          search-placeholder="Buscar projeto..."
          :use-company-filter="false"
          :control-class="'form-control'"
        />
      </b-form-group>

      <b-form-group label="Requisitos vinculados">
        <SearchableSelect
          v-model="localForm.requirements"
          :options="requirementOptions"
          multiple
          placeholder="Selecione um ou mais requisitos"
          search-placeholder="Buscar requisito..."
          :use-company-filter="false"
          :control-class="'form-control'"
        />
      </b-form-group>
    </section>

    <section class="grc-form__section">
      <div class="grc-form__section-header">
        <h3 class="grc-form__section-title">Tratamento e revisão</h3>
        <p class="grc-form__section-description">
          Registre a justificativa, o risco residual e o próximo ciclo de
          revisão.
        </p>
      </div>

      <b-form-group label="Justificativa do tratamento">
        <b-form-textarea
          v-model.trim="localForm.treatmentJustification"
          rows="2"
        ></b-form-textarea>
      </b-form-group>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Risco residual">
            <b-form-textarea
              v-model.trim="localForm.residualRisk"
              rows="2"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Apetite/Tolerância ao risco">
            <b-form-textarea
              v-model.trim="localForm.riskAppetite"
              rows="2"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Próxima revisão">
            <b-form-input
              v-model="localForm.nextReviewAt"
              type="date"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Motivo da atualização">
            <b-form-textarea
              v-model.trim="localForm.lastUpdateReason"
              rows="2"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="grc-form__toggle-hint">
        Defina se o risco segue ativo para monitoramento e tratamento contínuo.
      </div>
      <b-form-checkbox
        v-model="localForm.active"
        switch
        class="grc-form__toggle"
      >
        Risco ativo
      </b-form-checkbox>
    </section>

    <div class="grc-form__actions">
      <b-button variant="secondary" @click="$emit('cancel')">Cancelar</b-button>
      <b-button
        type="submit"
        variant="primary"
        :disabled="saving || Boolean(submitValidationError)"
      >
        {{ saving ? "Salvando..." : "Salvar" }}
      </b-button>
    </div>
  </b-form>
</template>

<script>
import SearchableSelect from "@/components/SearchableSelect.vue";

const GRCRiskStatus = {
  OPEN: 1,
  WAITING_APPROVAL: 2,
  CLOSED: 3,
  IN_REVIEW: 4,
};

export default {
  name: "GRCRiskForm",
  components: {
    SearchableSelect,
  },
  props: {
    risk: {
      type: Object,
      default: null,
    },
    areaOptions: {
      type: Array,
      default: () => [],
    },
    scopeOptions: {
      type: Array,
      default: () => [],
    },
    projectOptions: {
      type: Array,
      default: () => [],
    },
    userOptions: {
      type: Array,
      default: () => [],
    },
    requirementOptions: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localForm: this.buildForm(this.risk),
      statusValues: GRCRiskStatus,
    };
  },
  computed: {
    approvalLabel() {
      if (!this.risk || !this.risk.approval || !this.risk.approval.status) {
        return null;
      }
      const labels = {
        "not-required": "Sem aprovação obrigatória",
        pending: "Aguardando aprovação",
        approved: "Aprovado",
        rejected: "Rejeitado",
      };
      return labels[this.risk.approval.status] || this.risk.approval.status;
    },
    statusOptions() {
      return [
        this.buildStatusOption(this.statusValues.OPEN, "Aberto"),
        this.buildStatusOption(
          this.statusValues.WAITING_APPROVAL,
          "Aguardando aprovação",
          Boolean(this.localForm.secondLevelApprover),
          "exige aprovador de segundo nível"
        ),
        this.buildStatusOption(this.statusValues.CLOSED, "Fechado"),
        this.buildStatusOption(this.statusValues.IN_REVIEW, "Em revisão"),
      ];
    },
    submitValidationError() {
      if (
        this.localForm.status === this.statusValues.WAITING_APPROVAL &&
        !this.localForm.secondLevelApprover
      ) {
        return "Para salvar como aguardando aprovação, informe o aprovador de segundo nível.";
      }

      return "";
    },
    scaleOptions() {
      return [1, 2, 3, 4, 5].map((value) => ({ value, text: String(value) }));
    },
    treatmentOptions() {
      return [
        { value: "avoid", text: "Anular / Evitar" },
        { value: "reduce", text: "Reduzir / Mitigar" },
        { value: "share", text: "Compartilhar" },
        { value: "transfer", text: "Transferir" },
        { value: "accept", text: "Aceitar" },
      ];
    },
    consequenceOptions() {
      return [
        { value: "legal", text: "Legal" },
        { value: "reputational", text: "Reputacional" },
        { value: "financial", text: "Financeira" },
        { value: "operational", text: "Operacional" },
        { value: "other", text: "Outro" },
      ];
    },
    sourceTypeOptions() {
      return [
        { value: "legal-regulatory", text: "Legal/Regulatório" },
        { value: "contract", text: "Contrato" },
        { value: "internal-policy", text: "Política interna" },
        { value: "technical-standard", text: "Norma técnica" },
        { value: "best-practice", text: "Boa prática" },
        { value: "judicial-decision", text: "Decisão judicial" },
        { value: "other", text: "Outro" },
      ];
    },
    applicationTypeOptions() {
      return [
        { value: "all", text: "Todos" },
        { value: "sectors", text: "Setores" },
        { value: "stakeholders", text: "Stakeholders" },
        { value: "process", text: "Processo" },
      ];
    },
    scopeOptionsWithEmpty() {
      return [
        { value: null, text: "Selecione um escopo" },
        ...this.scopeOptions,
      ];
    },
    userOptionsWithEmpty() {
      return [
        { value: null, text: "Selecione um usuário" },
        ...this.userOptions,
      ];
    },
  },
  watch: {
    risk: {
      immediate: true,
      handler(value) {
        this.localForm = this.buildForm(value);
      },
    },
  },
  methods: {
    buildStatusOption(value, text, enabled = true, disabledHint = "") {
      return {
        value,
        text,
        disabled: !enabled && this.localForm.status !== value,
        ...(disabledHint && !enabled && this.localForm.status !== value
          ? { text: `${text} (${disabledHint})` }
          : {}),
      };
    },
    buildForm(risk) {
      return {
        title: risk?.title || "",
        internalCategory: risk?.internalCategory || "",
        event: risk?.event || "",
        causes: risk?.causes || "",
        consequences: risk?.consequences || "",
        consequenceType: risk?.consequenceType || "other",
        sourceType: risk?.sourceType || "legal-regulatory",
        sourceName: risk?.sourceName || "",
        sourceIdentifier: risk?.sourceIdentifier || "",
        jurisdiction: risk?.jurisdiction || "",
        applicationType: risk?.applicationType || "all",
        stakeholdersAffected: risk?.stakeholdersAffected || "",
        area: risk?.area?._id || risk?.area || null,
        scope: risk?.scope?._id || risk?.scope || null,
        projects: Array.isArray(risk?.projects)
          ? risk.projects.map((item) => item._id || item)
          : [],
        owner: risk?.owner?._id || risk?.owner || null,
        manager: risk?.manager?._id || risk?.manager || null,
        secondLevelApprover:
          risk?.secondLevelApprover?._id || risk?.secondLevelApprover || null,
        probability: risk?.probability || 1,
        impact: risk?.impact || 1,
        treatmentStrategy: risk?.treatmentStrategy || "reduce",
        treatmentJustification: risk?.treatmentJustification || "",
        residualRisk: risk?.residualRisk || "",
        riskAppetite: risk?.riskAppetite || "",
        requirements: Array.isArray(risk?.requirements)
          ? risk.requirements.map((item) => item._id || item)
          : [],
        periodicityMonths: risk?.periodicityMonths || "",
        nextReviewAt: this.formatDateForInput(risk?.nextReviewAt),
        lastUpdateReason: risk?.lastUpdateReason || "",
        status: risk?.status || GRCRiskStatus.OPEN,
        active: risk?.active !== false,
      };
    },
    formatDateForInput(value) {
      if (!value) {
        return null;
      }
      return new Date(value).toISOString().slice(0, 10);
    },
    normalizeId(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      const parsed = parseInt(value);
      return Number.isNaN(parsed) ? null : parsed;
    },
    submitForm() {
      if (this.submitValidationError) {
        return;
      }

      this.$emit("submit", {
        ...this.localForm,
        area: this.normalizeId(this.localForm.area),
        scope: this.normalizeId(this.localForm.scope),
        projects: (this.localForm.projects || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        owner: this.normalizeId(this.localForm.owner),
        manager: this.normalizeId(this.localForm.manager),
        secondLevelApprover: this.normalizeId(
          this.localForm.secondLevelApprover
        ),
        probability: parseInt(this.localForm.probability),
        impact: parseInt(this.localForm.impact),
        requirements: (this.localForm.requirements || [])
          .map((item) => this.normalizeId(item))
          .filter(Boolean),
        periodicityMonths: this.localForm.periodicityMonths
          ? parseInt(this.localForm.periodicityMonths)
          : null,
        status: parseInt(this.localForm.status),
        nextReviewAt: this.localForm.nextReviewAt || null,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use "./grc-form.scss";
</style>
